import { useState, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import { mangaList, categoryList } from '@/mocks/manga';

const categoryMap: Record<string, string> = {
  riman: '日漫',
  hanman: '韩漫',
  guoman: '国漫',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [sortBy, setSortBy] = useState<'latest' | 'rating' | 'views'>('latest');

  const currentCategory = categoryList.find((c) => c.slug === slug);

  const filteredManga = useMemo(() => {
    let list = slug === 'all' ? [...mangaList] : mangaList.filter((m) => m.category === slug || m.subCategory === slug);
    if (sortBy === 'latest') {
      list.sort((a, b) => new Date(b.updateTime).getTime() - new Date(a.updateTime).getTime());
    } else if (sortBy === 'rating') {
      list.sort((a, b) => b.rating - a.rating);
    } else if (sortBy === 'views') {
      list.sort((a, b) => b.views - a.views);
    }
    return list;
  }, [slug, sortBy]);

  const handleCategoryChange = (newSlug: string) => {
    navigate(`/category/${newSlug}`);
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <section className="py-8 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">首页</Link>
              <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center"></i>
              <span className="text-foreground-950 font-medium">
                {currentCategory ? currentCategory.name : '分类浏览'}
              </span>
            </nav>

            <div className="flex items-center gap-3 mb-6">
              <div className="w-1 h-5 bg-primary-500 rounded-full"></div>
              <h1 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                {currentCategory ? currentCategory.name : '分类浏览'}
              </h1>
              <span className="text-sm text-foreground-500">
                共 {filteredManga.length} 部漫画
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-6">
              {categoryList.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => handleCategoryChange(cat.slug)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-300 cursor-pointer whitespace-nowrap ${
                    slug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} w-3.5 h-3.5 flex items-center justify-center`}></i>
                  {cat.name}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-3 mb-6">
              <span className="text-sm text-foreground-500">排序：</span>
              {[
                { key: 'latest', label: '最新更新' },
                { key: 'rating', label: '评分最高' },
                { key: 'views', label: '人气最热' },
              ].map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setSortBy(opt.key as typeof sortBy)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    sortBy === opt.key
                      ? 'bg-secondary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5" data-product-shop="">
              {filteredManga.map((manga) => (
                <Link
                  key={manga.id}
                  to={`/manga/${manga.id}`}
                  className="group block"
                >
                  <div className="relative aspect-[3/4] rounded-lg overflow-hidden mb-3 bg-background-200">
                    <img
                      src={manga.cover}
                      alt={manga.title}
                      title={`${manga.title} - 樱花漫画 ${categoryMap[manga.category] || ''}`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-2 left-2">
                      <span className="px-2 py-0.5 rounded-md text-xs font-medium bg-primary-500 text-background-50">
                        {categoryMap[manga.category] || manga.category}
                      </span>
                    </div>
                    <div className="absolute top-2 right-2">
                      <button
                        className="w-7 h-7 flex items-center justify-center rounded-full bg-background-950/40 backdrop-blur-sm text-background-50 hover:bg-primary-500 transition-colors duration-200 cursor-pointer"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                      >
                        <i className="ri-heart-line text-sm w-3.5 h-3.5 flex items-center justify-center"></i>
                      </button>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3 pt-8">
                      <span className="text-xs text-background-200">{manga.status}</span>
                    </div>
                  </div>
                  <h3 className="font-heading text-sm font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors duration-200 line-clamp-1">
                    {manga.title}
                  </h3>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-foreground-500">{manga.author}</span>
                    <span className="text-xs text-accent-500 font-medium">
                      <i className="ri-star-fill w-3 h-3 inline-flex items-center justify-center mr-0.5"></i>
                      {manga.rating}
                    </span>
                  </div>
                </Link>
              ))}
            </div>

            {filteredManga.length === 0 && (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类下暂无漫画，请尝试其他分类</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}